/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import { useQuery } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table from 'components/Table'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { PageLoader } from 'components/PageLoader'
import { AITestResponseTypeEnum, AITestTypeEnum } from '__generatedTypes__/globalTypes'
import { FIND_ALL_AI_TEST_FOR_CMS } from 'queries/responseTests'
import {
  findAllAiTestForCms,
  findAllAiTestForCmsVariables,
  findAllAiTestForCms_findAllAiTestForCms,
} from '__generatedTypes__/findAllAiTestForCms'
import * as classes from './styles'

type Question = findAllAiTestForCms_findAllAiTestForCms & {
  id: string
}

type Props = {
  responseType: AITestResponseTypeEnum | null
  type: AITestTypeEnum
}

export const DataList = ({ type, responseType }: Props) => {
  const { getLink } = useLink()
  const { data, loading } = useQuery<findAllAiTestForCms, findAllAiTestForCmsVariables>(FIND_ALL_AI_TEST_FOR_CMS, {
    variables: {
      responseType,
      type,
    },
    fetchPolicy: 'cache-and-network',
  })

  const questions: Question[] = useMemo(() => {
    if (!data?.findAllAiTestForCms) return []

    const sorted = orderBy(data.findAllAiTestForCms || [], (a) => Number(a.order), 'desc')
    return sorted.map((test) => ({
      id: `${test.testId} - ${test.questionId}`,
      ...test,
    }))
  }, [data?.findAllAiTestForCms])

  if (loading && !questions.length) {
    return <PageLoader />
  }

  return (
    <div css={classes.tablesWrapper}>
      <Table<Question>
        css={classes.table}
        columns={[
          {
            title: 'Title',
            renderCell: (test) => `${test.testQuestionTitle}`,
          },
          {
            title: 'GPT Grades',
            renderCell: (test) => (
              <Link
                to={getLink(
                  cmsRoutes.GPT_GRADE_EDITOR,
                  { id: test.questionId },
                  new URLSearchParams({ title: test.testQuestionTitle, question: test.question, type })
                )}
              >
                <IconButton size="small" css={classes.link}>
                  {test.gptGrades}

                  <EditIcon />
                </IconButton>
              </Link>
            ),
          },
          {
            title: 'Training Grades',
            renderCell: (test) => (
              <Link
                to={getLink(
                  cmsRoutes.TRAINING_GRADE_EDITOR,
                  { id: test.questionId },
                  new URLSearchParams({ title: test.testQuestionTitle, question: test.question, type })
                )}
              >
                <IconButton size="small" css={classes.link}>
                  {test.trainingGrades}

                  <EditIcon />
                </IconButton>
              </Link>
            ),
          },
          {
            title: 'Question',
            renderCell: (test) => <span css={classes.ellipsis}>{test.question}</span>,
            flex: '1 1 250px',
          },
        ]}
        items={questions}
      />
    </div>
  )
}
